import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { supabase, createClerkSupabaseClient } from '@/lib/supabase'
import { useUser } from '@/lib/hooks'
import { useOrganization } from '@/lib/hooks/useOrganization'
import { useGetToken } from '@/lib/hooks/use-auth'

interface Project {
  id: string
  name: string
  description: string | null
  company_name: string | null
  incorporation_date: string | null
  total_shares_authorized: number | null
  created_by: string | null
  created_at: string | null
  updated_at: string | null
}

interface ProjectMember {
  id: string
  project_id: string
  user_id: string
  role: string
  joined_at: string
}

interface ProjectContextType {
  currentProject: Project | null
  setCurrentProject: (project: Project | null) => void
  projects: Project[]
  projectsLoading: boolean
  projectMembers: ProjectMember[]
  membersLoading: boolean
  switchProject: (projectId: string) => Promise<void>
  createProject: (name: string, description?: string, companyName?: string) => Promise<Project>
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>
  deleteProject: (projectId: string) => Promise<void>
  inviteToProject: (projectId: string, email: string, role?: string) => Promise<void>
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined)

export function ProjectProvider({ children }: { children: ReactNode }) {
  const [currentProject, setCurrentProject] = useState<Project | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [projectMembers, setProjectMembers] = useState<ProjectMember[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const { profile: user } = useUser()
  const { currentOrganization } = useOrganization()
  const getToken = useGetToken()

  // Load user's projects when organization changes
  useEffect(() => {
    if (user && currentOrganization) {
      loadProjects()
    }
  }, [user, currentOrganization])

  // Load project members when current project changes
  useEffect(() => {
    if (currentProject) {
      loadProjectMembers(currentProject.id)
    }
  }, [currentProject])

  const loadProjects = async () => {
    if (!user?.id || !currentOrganization?.id) return
    
    try {
      setProjectsLoading(true)
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      // Load projects in the current organization where the user is a member
      const { data, error } = await authenticatedClient
        .from('project_members')
        .select(`
          project_id,
          role,
          joined_at,
          projects!inner (*)
        `)
        .eq('user_id', user.id)
        .eq('projects.organization_id', currentOrganization.id)
        .order('joined_at', { ascending: false })

      if (error) throw error

      // Extract projects from the join result
      const userProjects = data?.map(pm => ({
        ...pm.projects,
        userRole: pm.role // Add user's role in this project
      })) || []

      setProjects(userProjects as Project[])
      
      // Set the first project as current if none selected
      if (userProjects && userProjects.length > 0 && !currentProject) {
        setCurrentProject(userProjects[0] as Project)
      }
    } catch (error) {
      console.error('Error loading projects:', error)
    } finally {
      setProjectsLoading(false)
    }
  }

  const loadProjectMembers = async (projectId: string) => {
    try {
      setMembersLoading(true)
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
        .from('project_members')
        .select('*')
        .eq('project_id', projectId)

      if (error) throw error
      
      // Filter out any null values and cast properly
      const validMembers = (data || []).filter((member): member is ProjectMember => 
        member.project_id !== null && member.user_id !== null
      )
      
      setProjectMembers(validMembers)
    } catch (error) {
      console.error('Error loading project members:', error)
    } finally {
      setMembersLoading(false)
    }
  }

  const switchProject = async (projectId: string) => {
    const project = projects.find(p => p.id === projectId)
    if (project) {
      setCurrentProject(project)
      // Store in localStorage for persistence
      localStorage.setItem('currentProjectId', projectId)
    }
  }

  const createProject = async (name: string, description?: string, companyName?: string) => {
    if (!user) throw new Error('User not authenticated')

    // Create authenticated Supabase client with Clerk token
    const authenticatedClient = createClerkSupabaseClient(getToken)

    // First, check if the user exists in the users table
    const { data: userExists, error: userCheckError } = await authenticatedClient
      .from('users')
      .select('id')
      .eq('id', user.id)
      .single()

    if (userCheckError || !userExists) {
      // If user doesn't exist, create them
      const { error: createUserError } = await authenticatedClient
        .from('users')
        .insert({
          id: user.id,
          email: user.email || '',
          role_id: null // Default role
        })

      if (createUserError) {
        console.error('Failed to create user profile:', createUserError)
        throw new Error('Failed to create user profile. Please try logging out and back in.')
      }
    }

    // Use the atomic function to create project with owner and tasks
    const { data: project, error: projectError } = await authenticatedClient
      .rpc('create_project_with_owner', {
        p_name: name,
        p_description: description,
        p_company_name: companyName,
        p_organization_id: currentOrganization?.id || undefined,
        p_user_id: user.id
      })

    if (projectError) {
      console.error('Project creation error:', projectError)
      if (projectError.code === '23503' && projectError.message.includes('projects_created_by_fkey')) {
        throw new Error('User profile not found. Please try logging out and logging back in.')
      }
      throw projectError
    }

    // Reload projects
    await loadProjects()
    
    return project
  }

  const updateProject = async (projectId: string, updates: Partial<Project>) => {
    // Create authenticated Supabase client with Clerk token
    const authenticatedClient = createClerkSupabaseClient(getToken)
    
    const { error } = await authenticatedClient
      .from('projects')
      .update(updates)
      .eq('id', projectId)

    if (error) throw error

    // Reload projects
    await loadProjects()
  }

  const deleteProject = async (projectId: string) => {
    // Check if this is the last project
    if (projects.length <= 1) {
      throw new Error('Cannot delete the last project')
    }

    // Create authenticated Supabase client with Clerk token
    const authenticatedClient = createClerkSupabaseClient(getToken)

    const { error } = await authenticatedClient
      .from('projects')
      .delete()
      .eq('id', projectId)

    if (error) throw error

    // If we deleted the current project, switch to another one
    if (currentProject?.id === projectId) {
      const remainingProjects = projects.filter(p => p.id !== projectId)
      if (remainingProjects.length > 0) {
        setCurrentProject(remainingProjects[0])
        localStorage.setItem('currentProjectId', remainingProjects[0].id)
      }
    }

    // Reload projects
    await loadProjects()
  }

  const inviteToProject = async (_projectId: string, _email: string, _role: string = 'member') => {
    // This will be implemented later with the invitation system
    // For now, we'll just throw an error
    throw new Error('Project invitations not yet implemented')
  }

  // Load saved project from localStorage
  useEffect(() => {
    const savedProjectId = localStorage.getItem('currentProjectId')
    if (savedProjectId && projects.length > 0) {
      const savedProject = projects.find(p => p.id === savedProjectId)
      if (savedProject) {
        setCurrentProject(savedProject)
      }
    }
  }, [projects])

  return (
    <ProjectContext.Provider value={{
      currentProject,
      setCurrentProject,
      projects,
      projectsLoading,
      projectMembers,
      membersLoading,
      switchProject,
      createProject,
      updateProject,
      deleteProject,
      inviteToProject
    }}>
      {children}
    </ProjectContext.Provider>
  )
}

export function useProject() {
  const context = useContext(ProjectContext)
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider')
  }
  return context
}